// B1012: current-branch store — the branch twin of db-store.js.
//
// Holds the session's selected branch and drives `USE BRANCH <name>` over the
// shared WebSocket, so every query on that connection (query console, schema
// browser, …) reads the chosen branch. A branch belongs to one database
// (`SHOW BRANCHES` lists the branches of the session's database), so a
// database switch puts the session back on `main`. A transparent reconnect
// re-applies the selection, the same way db-store re-applies `USE <db>`.

import { wsSql, onSocketOpen } from './socket.js';
import { DEFAULT_DB, getCurrentDb, subscribeCurrentDb } from './db-store.js';

export const DEFAULT_BRANCH = 'main';

let _current = DEFAULT_BRANCH;
const _listeners = new Set();

function notify() {
  for (const cb of _listeners) {
    try { cb(_current); } catch { /* listener error */ }
  }
}

export function getCurrentBranch() { return _current; }

export function subscribeCurrentBranch(cb) {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}

// Quote a branch name for SQL. The parser's `expect_db_name` (shared by
// CREATE / DROP / USE BRANCH) accepts a bare identifier or a double-quoted
// name; simple names go bare, anything else is double-quoted with embedded
// quotes doubled.
export function quoteBranchIdent(name) {
  if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) return name;
  return '"' + String(name).replace(/"/g, '""') + '"';
}

// SHOW BRANCHES → [{branch, base_commit, materialized, current}], the server's
// order (main first, then by name). `current` is the SESSION's branch as the
// server sees it. An empty answer still lists `main`: it exists implicitly.
export function parseBranches(r) {
  const cols = r?.columns ?? [];
  const rows = (r?.data ?? []).map((row) => {
    const o = {};
    cols.forEach((c, i) => { o[c] = row[i]; });
    return {
      branch: o.branch,
      base_commit: o.base_commit ?? '-',
      materialized: typeof o.materialized === 'number' ? o.materialized : null,
      current: o.current === true,
    };
  });
  if (rows.length === 0) {
    rows.push({ branch: DEFAULT_BRANCH, base_commit: '-', materialized: null, current: true });
  }
  return rows;
}

export async function listBranches() {
  return parseBranches(await wsSql('SHOW BRANCHES'));
}

// Switch the session's branch. Throws with the server's message if the branch
// does not exist in the current database; the selection is then unchanged.
export async function setCurrentBranch(name) {
  await wsSql(`USE BRANCH ${quoteBranchIdent(name)}`);
  if (_current !== name) { _current = name; notify(); }
}

export async function createBranch(name) {
  await wsSql(`CREATE BRANCH ${quoteBranchIdent(name)}`);
}

// Drop a branch. If it is the current one, fall back to `main` first so the
// session is not left on a gone branch.
export async function dropBranch(name) {
  if (name === DEFAULT_BRANCH) throw new Error('the main branch cannot be dropped');
  if (_current === name) await setCurrentBranch(DEFAULT_BRANCH);
  await wsSql(`DROP BRANCH ${quoteBranchIdent(name)}`);
}

// A database switch puts the session back on `main`: a branch is scoped to
// its database, and the server keeps the connection's branch name across
// `USE <db>`, so the name is sent again against the new database.
subscribeCurrentDb(() => {
  if (_current !== DEFAULT_BRANCH) { _current = DEFAULT_BRANCH; notify(); }
  wsSql(`USE BRANCH ${DEFAULT_BRANCH}`).catch(() => { /* best-effort */ });
});

// Re-apply after a transparent reconnect: a brand-new connection starts on
// `main`. db-store's own re-apply of `USE <db>` was registered first (this
// module imports it), and the socket answers requests in order, so the
// branch lands in the right database.
onSocketOpen(() => {
  if (_current !== DEFAULT_BRANCH && getCurrentDb() !== undefined) {
    wsSql(`USE BRANCH ${quoteBranchIdent(_current)}`).catch(() => { /* best-effort */ });
  }
});

export { DEFAULT_DB };
